import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaGraduationCap, FaBriefcase, FaRocket, FaCode,
  FaBook, FaGamepad, FaShoppingCart, FaExternalLinkAlt,
  FaCalendarAlt, FaMapMarkerAlt, FaChevronDown, FaFilter,
  FaCertificate, FaLaptopCode, FaUniversity, FaTrophy,
  FaUtensils, FaTasks, FaHospital, FaMoneyBillWave,
  FaMapMarkedAlt, FaHandHoldingHeart, FaTimes
} from 'react-icons/fa';
import './Timeline.css';

/* ------------------------------------------------------------------ */
/*  STAGE GEOMETRY                                                    */
/* ------------------------------------------------------------------ */
const FAN_OFFSET = 200;

const GEO = {
  desktop: {
    W: 1600, H: 800,
    pivot: { x: 1053, y: 58 },
    dial: { cx: 1055, cy: -60 },
    p0: { x: 372 + FAN_OFFSET, y: 85 },
    p1: { x: 800 + FAN_OFFSET, y: 300 },
    p2: { x: 1280 + FAN_OFFSET, y: 290 },
    label: { dx: 30, dy: -26 },
    card: (i, n) => {
      const gap = n > 1 ? (900 - FAN_OFFSET * 0.6) / (n - 1) : 0;
      return { x: 300 + FAN_OFFSET + i * gap, y: 150 + (n > 1 ? (i * 200) / (n - 1) : 0) };
    }
  },
  mobile: {
    W: 800, H: 1300,
    pivot: { x: 520, y: 50 },
    dial: { cx: 520, cy: -70 },
    p0: { x: 60, y: 300 },
    p1: { x: 400, y: 360 },
    p2: { x: 720, y: 250 },
    label: { dx: 0, dy: -24 },
    card: (i) => ({ x: 250 + i * 70, y: 330 + i * 105 })
  }
};

// point on the quadratic curve (t = 0..1)
const curvePoint = (g, t) => {
  const a = (1 - t) * (1 - t);
  const b = 2 * t * (1 - t);
  const c = t * t;
  return {
    x: a * g.p0.x + b * g.p1.x + c * g.p2.x,
    y: a * g.p0.y + b * g.p1.y + c * g.p2.y
  };
};

/* short text shown on each year card (like the video) */
const YEAR_INFO = {
  '2019': { label: 'Foundation',    lines: ['BSc Mathematics', 'Logic first', 'Strong base'] },
  '2021': { label: 'Beginning',     lines: ['First line of code', 'C++ & Java', 'Bigger dreams'] },
  '2022': { label: 'Exploration',   lines: ['BSc done · 82%', 'MCA started', 'Found direction'] },
  '2023': { label: 'Practice',      lines: ['16+ certificates', 'E-Book Library', 'Kept building'] },
  '2024': { label: 'Growth',        lines: ['MCA · 8.02 CGPA', '8 internships', 'Real projects'] },
  '2025': { label: 'Opportunities', lines: ['Bankify', 'ShopSmart', 'TaskFlow'] },
  '2026': { label: 'Next Chapter',  lines: ['AI + Full Stack', 'More impact', 'Still designing'] }
};
const DEFAULT_INFO = { label: 'Milestone', lines: ['Learning', 'Building', 'Growing'] };

const timelineData = [
  // ===== 2026 =====
  {
    year: "2026",
    items: [
      {
        id: 1,
        icon: <FaMapMarkedAlt />,
        title: "Bhoomi Seva",
        subtitle: "Smart Land Records & AI Price Prediction System",
        role: "Full Stack & ML Developer | Production Project",
        date: "September 2026",
        location: "Remote",
        description: "Developed an enterprise-grade land records portal for Maharashtra featuring digital 7/12 Satbara, 8-A Extract & Property Card generation. Integrated an XGBoost Machine Learning valuation engine for real-time market price prediction based on geospatial & proximity parameters.",
        highlights: [
          "Built AI land valuation engine using XGBoost & Random Forest with Ready Reckoner comparison",
          "Automated digital generation & PDF download for 7/12 Satbara, 8-A Extract & Property Cards",
          "Implemented JWT security with interceptor-driven auto-logout for blocked/deleted users",
          "Designed 11-table normalized schema hosted on TiDB Cloud Serverless MySQL",
          "Created Admin Panel for land record CRUD, document issuance & support desk",
          "Deployed 3-tier architecture (Spring Boot + Python Flask ML + React) on Render"
        ],
        tech: ["Spring Boot 3", "React 18", "Python", "Flask", "XGBoost", "MySQL (TiDB)", "JWT", "Tailwind CSS"],
        type: "project",
        link: "https://bhoomi-frontend-f5sw.onrender.com/",
        color: "#F97316"
      },
      {
        id: 2,
        icon: <FaHandHoldingHeart />,
        title: "Majhi Yojna",
        subtitle: "AI-Powered Government Scheme Recommendation System",
        role: "Full Stack & AI Developer | Production Project",
        date: "October 2026",
        location: "Remote",
        description: "Built an intelligent AI recommendation engine matching Maharashtra citizens with relevant government schemes using rule-based filtering and Cosine Similarity ML ranking. Features profile completion scoring, life-event suggestions, scheme comparison & family dashboard.",
        highlights: [
          "Engineered hybrid recommendation engine (30+ rules + Cosine Similarity ML ranking)",
          "Designed 11-field profile completion engine that unlocks AI recommendations above threshold",
          "Created life-event suggestion engine for seniors, women, farmers, students & BPL families",
          "Implemented real-time Admin notification trigger on scheme creation, updates & deletion",
          "Built multi-role access control (USER/ADMIN) with JWT and auto-session cleanup",
          "Deployed 3-tier system (Spring Boot + Flask ML + React + TiDB MySQL) on Render"
        ],
        tech: ["Spring Boot 3", "React 18", "Python", "Flask", "Scikit-Learn", "MySQL (TiDB)", "JWT"],
        type: "project",
        link: "https://majhi-yojna-frontend.onrender.com/",
        color: "#0F766E"
      },
      {
        id: 3,
        icon: <FaHospital />,
        title: "MediBook Pro",
        subtitle: "Healthcare Appointment Management System",
        role: "Full Stack Developer | Production Project",
        date: "February 2026",
        location: "Remote",
        description: "Built enterprise-grade healthcare platform enabling seamless doctor-patient interactions. This production-ready system digitizes the entire hospital appointment workflow, from doctor discovery to consultation completion with secure payment processing.",
        highlights: [
          "Developed 15+ REST APIs with <100ms average response time",
          "Achieved 99.9% uptime on production environment",
          "Implemented JWT authentication with role-based access control",
          "Integrated Razorpay payment gateway for secure transactions",
          "Built SMS notification system using Twilio API",
          "Created admin dashboard with comprehensive analytics"
        ],
        tech: ["Spring Boot 3", "React 18", "MySQL", "Redis", "JWT", "Razorpay", "Twilio"],
        type: "project",
        link: "https://medibook-pro-front.onrender.com/",
        color: "#00D9A5"
      },
      {
        id: 4,
        icon: <FaUtensils />,
        title: "FoodDelivery Pro",
        subtitle: "MERN Stack Food Delivery Platform",
        role: "Full Stack Developer",
        date: "January 2026",
        location: "Personal Project",
        description: "Developed a comprehensive food ordering and delivery platform connecting hungry customers with local restaurants. The platform handles complete order lifecycle from browsing menus to delivery tracking with real-time updates.",
        highlights: [
          "Built complete order lifecycle management system",
          "Created restaurant partner dashboard for menu management",
          "Implemented real-time order status tracking",
          "Integrated Stripe payment gateway",
          "Added multi-restaurant cart support",
          "Developed reviews and ratings system"
        ],
        tech: ["React.js", "Node.js", "Express.js", "MongoDB", "Stripe", "Redux"],
        type: "project",
        link: "https://food-delivery-online.vercel.app/",
        color: "#EF4444"
      }
    ]
  },

  // ===== 2025 =====
  {
    year: "2025",
    items: [
      {
        id: 5,
        icon: <FaMoneyBillWave />,
        title: "Bankify",
        subtitle: "Modern Digital Banking Platform",
        role: "Full Stack Developer",
        date: "November 2025",
        location: "Personal Project",
        description: "Developed a secure digital banking solution with bank-grade security protocols. The platform enables users to manage accounts, perform real-time money transfers, handle card services, and track finances with comprehensive audit trails.",
        highlights: [
          "Implemented bank-grade security with AES-256 encryption",
          "Built multi-layer authentication with JWT and 2FA",
          "Created real-time transaction processing with ACID compliance",
          "Developed complete audit trail for regulatory compliance",
          "Added virtual card management system",
          "Implemented fraud detection monitoring"
        ],
        tech: ["Spring Boot", "React.js", "Redux", "MySQL", "Spring Security", "JWT"],
        type: "project",
        link: "https://bankify-demo-digital-banking-system.vercel.app/",
        color: "#667EEA"
      },
      {
        id: 6,
        icon: <FaTasks />,
        title: "TaskFlow Ultimate",
        subtitle: "Project Management Platform",
        role: "Full Stack Developer",
        date: "August 2025",
        location: "Personal Project",
        description: "Built a comprehensive project management solution inspired by Trello and Jira. The platform enables teams to create tasks, manage projects, track deadlines, and collaborate effectively with real-time updates using WebSockets.",
        highlights: [
          "Implemented real-time collaboration with Socket.io",
          "Built drag-and-drop Kanban boards using React DnD",
          "Created team workspaces with role management",
          "Added deadline tracking with email notifications",
          "Developed activity feed and audit logs",
          "Built progress dashboard with analytics"
        ],
        tech: ["React.js", "Node.js", "Express.js", "MongoDB", "Socket.io", "React DnD"],
        type: "project",
        link: "https://taskflow-frontend-ziha.onrender.com/",
        color: "#8B5CF6"
      },
      {
        id: 7,
        icon: <FaShoppingCart />,
        title: "ShopSmart",
        subtitle: "Microservices E-Commerce Platform",
        role: "Full Stack Developer",
        date: "May 2025",
        location: "Personal Project",
        description: "Developed a scalable e-commerce platform using microservices architecture. The system features independent services for users, products, orders, and payments, enabling high availability, independent deployment, and horizontal scaling.",
        highlights: [
          "Designed 4 independent microservices architecture",
          "Implemented API Gateway with Spring Cloud Gateway",
          "Configured Service Discovery with Eureka Server",
          "Built circuit breaker pattern for fault tolerance",
          "Deployed services using Docker containers",
          "Implemented Saga pattern for distributed transactions"
        ],
        tech: ["Spring Boot", "Microservices", "Docker", "MySQL", "Spring Cloud", "Eureka"],
        type: "project",
        link: "https://shopssmart-ecommerce.vercel.app/",
        color: "#F59E0B"
      }
    ]
  },

  // ===== 2024 =====
  {
    year: "2024",
    items: [
      {
        id: 8,
        icon: <FaGraduationCap />,
        title: "MCA Completed",
        subtitle: "Master of Computer Applications",
        role: "Post Graduate Degree | Sant Gadge Baba Amravati University",
        date: "June 2024",
        location: "Amravati, Maharashtra",
        description: "Successfully completed Master of Computer Applications with 8.02 CGPA. The 2-year intensive program provided deep expertise in software development, database management, and emerging technologies. Gained hands-on experience through multiple projects and industry-oriented curriculum.",
        highlights: [
          "Graduated with 8.02 CGPA (First Class with Distinction)",
          "Specialized in Full Stack Web Development",
          "Completed major project on Healthcare Management System",
          "Studied Advanced Java, Spring Framework & React.js",
          "Learned System Design and Software Architecture",
          "Participated in coding competitions and hackathons"
        ],
        tech: ["Java", "Spring Boot", "React.js", "MySQL", "System Design", "Data Structures"],
        type: "education",
        color: "#4ECDC4"
      },
      {
        id: 9,
        icon: <FaGamepad />,
        title: "GuessPro",
        subtitle: "Interactive Number Guessing Game",
        role: "Frontend Developer",
        date: "July 2024",
        location: "Personal Project",
        description: "Built an engaging browser-based number guessing game to demonstrate core JavaScript skills. The game features multiple difficulty levels, score tracking, competitive leaderboard, and responsive design with sound effects and animations.",
        highlights: [
          "Implemented game logic with pure vanilla JavaScript",
          "Created multiple difficulty levels (Easy, Medium, Hard)",
          "Built scoring system with streak tracking",
          "Added persistent high score leaderboard using LocalStorage",
          "Implemented sound effects and visual animations",
          "Created fully responsive design for all devices"
        ],
        tech: ["JavaScript ES6+", "HTML5", "CSS3", "DOM Manipulation", "LocalStorage"],
        type: "project",
        link: "https://guess-my-number-game-two-jet.vercel.app/",
        color: "#EC4899"
      },
      {
        id: 10,
        icon: <FaBriefcase />,
        title: "Virtual Internships",
        subtitle: "8 Fortune 500 Companies | Industry Experience",
        role: "Software Engineering Intern",
        date: "January - June 2024",
        location: "Remote",
        description: "Completed virtual internship programs with top Fortune 500 companies, gaining hands-on experience in software development, data science, agile methodologies, and industry best practices. These programs provided real-world exposure to enterprise software development.",
        highlights: [
          "JPMorgan Chase - Software Engineering Virtual Experience",
          "Tata Group - Data Visualization Job Simulation",
          "Walmart Global Tech - Advanced Software Engineering",
          "Cognizant - Agile Methodology Program",
          "Accenture - Developer & Technology Job Simulation",
          "Goldman Sachs - Software Engineering Virtual Program"
        ],
        tech: ["Python", "Java", "Data Science", "Agile", "Machine Learning", "Git"],
        type: "internship",
        color: "#BB8FCE"
      }
    ]
  },

  // ===== 2023 =====
  {
    year: "2023",
    items: [
      {
        id: 11,
        icon: <FaBook />,
        title: "E-Book Library Pro",
        subtitle: "Digital Library Management System",
        role: "Web Developer",
        date: "August 2023",
        location: "Academic Project",
        description: "Developed an online digital library platform as part of academic curriculum. The system enables users to discover, browse, and read ebooks with comprehensive category-based organization and a user-friendly reading interface.",
        highlights: [
          "Built complete book catalog with search functionality",
          "Implemented category-based filtering system",
          "Created user-friendly online reading interface",
          "Added checkout and order history features",
          "Designed responsive layout for all devices",
          "Implemented user authentication system"
        ],
        tech: ["HTML5", "CSS3", "JavaScript", "Bootstrap", "LocalStorage"],
        type: "project",
        link: "https://ebook-library-online.vercel.app/",
        color: "#10B981"
      },
      {
        id: 12,
        icon: <FaCertificate />,
        title: "Professional Certifications",
        subtitle: "16+ Industry Certifications Earned",
        role: "Continuous Learning & Skill Development",
        date: "Throughout 2023",
        location: "Online Learning Platforms",
        description: "Dedicated the year to professional skill development by completing comprehensive certifications from leading tech companies. These certifications validate expertise in Full Stack Development, Cybersecurity, Artificial Intelligence, Cloud Computing, and Data Analytics.",
        highlights: [
          "Infosys Springboard - Full Stack Java Development",
          "Cisco Networking Academy - Cybersecurity Essentials",
          "Microsoft Learn - AI Fundamentals (AI-900)",
          "Google - Data Analytics Professional Certificate",
          "LinkedIn Learning - React.js Essential Training",
          "HackerRank - Java, SQL, Problem Solving Certificates"
        ],
        tech: ["Full Stack", "Cybersecurity", "AI/ML", "Cloud Computing", "Data Analytics"],
        type: "certification",
        color: "#F7DC6F"
      }
    ]
  },

  // ===== 2022 =====
  {
    year: "2022",
    items: [
      {
        id: 13,
        icon: <FaUniversity />,
        title: "MCA Started",
        subtitle: "Master of Computer Applications",
        role: "Post Graduate Student | Sant Gadge Baba Amravati University",
        date: "August 2022",
        location: "Amravati, Maharashtra",
        description: "Enrolled in Master of Computer Applications (MCA) program to pursue advanced studies in computer science. The program focuses on software development, database management, web technologies, and preparing for industry-ready careers in IT.",
        highlights: [
          "Started Advanced Java Programming (Core & Advanced)",
          "Began learning Database Management Systems (DBMS)",
          "Introduced to Web Technologies (HTML, CSS, JS)",
          "Studied Data Structures and Algorithms",
          "Learned Object-Oriented Programming concepts",
          "Started Software Engineering fundamentals"
        ],
        tech: ["Java", "DBMS", "Web Technologies", "Data Structures", "OOP"],
        type: "education",
        color: "#4ECDC4"
      },
      {
        id: 14,
        icon: <FaTrophy />,
        title: "BSc Mathematics Completed",
        subtitle: "Bachelor of Science in Mathematics",
        role: "Undergraduate Degree | Sant Gadge Baba Amravati University",
        date: "June 2022",
        location: "Amravati, Maharashtra",
        description: "Successfully completed Bachelor of Science in Mathematics with 82% marks (First Class). The 3-year program developed strong analytical thinking, logical reasoning, and problem-solving skills through rigorous study of advanced mathematics, statistics, and related sciences.",
        highlights: [
          "Graduated with 82% marks (First Class)",
          "Developed strong analytical and logical thinking",
          "Mastered calculus, algebra, and statistics",
          "Built foundation for programming logic",
          "Enhanced problem-solving abilities",
          "Gained expertise in mathematical modeling"
        ],
        tech: ["Mathematics", "Statistics", "Calculus", "Algebra", "Analytical Skills"],
        type: "education",
        color: "#4ECDC4"
      }
    ]
  },

  // ===== 2021 =====
  {
    year: "2021",
    items: [
      {
        id: 15,
        icon: <FaLaptopCode />,
        title: "Started Coding Journey",
        subtitle: "Self-Learning Programming & Web Development",
        role: "Aspiring Developer | Self-Taught",
        date: "2021",
        location: "Self-Paced Learning",
        description: "Embarked on the journey of learning programming and web development through online resources, tutorials, and hands-on practice. This year marked the beginning of transforming mathematical problem-solving skills into coding abilities through dedicated self-study.",
        highlights: [
          "Learned C++ fundamentals and basic programming",
          "Started Java programming language",
          "Built first HTML/CSS web pages",
          "Introduced to JavaScript basics",
          "Solved beginner coding problems on platforms",
          "Developed passion for software development"
        ],
        tech: ["C++", "Java Basics", "HTML/CSS", "JavaScript Basics", "Problem Solving"],
        type: "learning",
        color: "#45B7D1"
      }
    ]
  },

  // ===== 2019 =====
  {
    year: "2019",
    items: [
      {
        id: 16,
        icon: <FaGraduationCap />,
        title: "BSc Mathematics Started",
        subtitle: "Bachelor of Science in Mathematics",
        role: "Undergraduate Student | Sant Gadge Baba Amravati University",
        date: "July 2019",
        location: "Amravati, Maharashtra",
        description: "Enrolled in Bachelor of Science (BSc) program with Mathematics as the major subject. This 3-year undergraduate program laid the foundation for logical thinking, analytical reasoning, and mathematical problem-solving skills that would later prove invaluable in programming.",
        highlights: [
          "Started undergraduate studies in Mathematics",
          "Learned foundational mathematics concepts",
          "Developed logical and analytical thinking",
          "Studied Physics and Chemistry as allied subjects",
          "Built strong problem-solving foundation",
          "Prepared base for future technical education"
        ],
        tech: ["Mathematics", "Physics", "Chemistry", "Statistics", "Logic"],
        type: "education",
        color: "#4ECDC4"
      }
    ]
  }
];

const filterCategories = [
  { id: 'all', label: 'All', icon: <FaFilter /> },
  { id: 'project', label: 'Projects', icon: <FaRocket /> },
  { id: 'education', label: 'Education', icon: <FaGraduationCap /> },
  { id: 'certification', label: 'Certifications', icon: <FaCertificate /> },
  { id: 'internship', label: 'Internships', icon: <FaBriefcase /> },
  { id: 'learning', label: 'Learning', icon: <FaCode /> }
];

const getTypeInfo = (type) => {
  const types = {
    project: { label: 'Project', color: '#ff6b6b', bg: 'rgba(255, 107, 107, 0.15)' },
    education: { label: 'Education', color: '#4ecdc4', bg: 'rgba(78, 205, 196, 0.15)' },
    learning: { label: 'Learning', color: '#45b7d1', bg: 'rgba(69, 183, 209, 0.15)' },
    certification: { label: 'Certification', color: '#f7dc6f', bg: 'rgba(247, 220, 111, 0.15)' },
    internship: { label: 'Internship', color: '#bb8fce', bg: 'rgba(187, 143, 206, 0.15)' }
  };
  return types[type] || { label: type, color: '#64ffda', bg: 'rgba(100, 255, 218, 0.15)' };
};

const hideBrokenImage = (e) => {
  e.currentTarget.style.display = 'none';
};

const sortedYears = [...timelineData].sort((a, b) => Number(a.year) - Number(b.year));

const Timeline = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeYear, setActiveYear] = useState(sortedYears[sortedYears.length - 1].year);
  const [drawerYear, setDrawerYear] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const stageRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // geometry for the current screen
  const g = isMobile ? GEO.mobile : GEO.desktop;
  const { W, H } = g;
  const PIVOT = g.pivot;
  const ARC_PATH = `M${g.p0.x},${g.p0.y} Q${g.p1.x},${g.p1.y} ${g.p2.x},${g.p2.y}`;

  const matches = (item) => activeFilter === 'all' || item.type === activeFilter;
  const availableYears = sortedYears
    .filter((y) => y.items.some(matches))
    .map((y) => y.year);

  const n = sortedYears.length;
  const nodes = sortedYears.map((y, i) => ({
    year: y.year,
    ...curvePoint(g, 0.06 + (0.92 * i) / (n - 1))
  }));
  const activeIndex = Math.max(0, sortedYears.findIndex((y) => y.year === activeYear));
  const activeNode = nodes[activeIndex];

  // counts for the footer stats
  const counts = { all: 0 };
  timelineData.forEach((y) =>
    y.items.forEach((item) => {
      counts.all += 1;
      counts[item.type] = (counts[item.type] || 0) + 1;
    })
  );

  // if the selected year has nothing for the current filter -> jump to the closest valid one
  useEffect(() => {
    const avail = sortedYears
      .filter((y) => y.items.some((it) => activeFilter === 'all' || it.type === activeFilter))
      .map((y) => y.year);
    if (avail.length && !avail.includes(activeYear)) {
      setActiveYear(avail[avail.length - 1]);
    }
  }, [activeFilter, activeYear]);

  // keyboard: Esc closes drawer, arrows travel through time
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setDrawerYear(null);
        return;
      }
      if (drawerYear) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const avail = sortedYears
          .filter((y) => y.items.some((it) => activeFilter === 'all' || it.type === activeFilter))
          .map((y) => y.year);
        const idx = avail.indexOf(activeYear);
        const next = avail[idx + (e.key === 'ArrowRight' ? 1 : -1)];
        if (next) setActiveYear(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerYear, activeYear, activeFilter]);

  // full-width page: hide the tiny sideways scrollbar that 100vw can cause
  useEffect(() => {
    const prev = document.documentElement.style.overflowX;
    document.documentElement.style.overflowX = 'hidden';
    return () => {
      document.documentElement.style.overflowX = prev;
    };
  }, []);

  // lock page scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerYear ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerYear]);

  // thin guide line from the dial knob to the mouse
  const onStageMove = (e) => {
    if (isMobile) return;
    const stage = stageRef.current;
    const line = trackRef.current;
    if (!stage || !line) return;
    const r = stage.getBoundingClientRect();
    line.setAttribute('x2', ((e.clientX - r.left) / r.width) * W);
    line.setAttribute('y2', ((e.clientY - r.top) / r.height) * H);
    line.style.opacity = 0.55;
  };
  const onStageLeave = () => {
    if (trackRef.current) trackRef.current.style.opacity = 0;
  };

  const selectYear = (year, open) => {
    if (!availableYears.includes(year)) return;
    setActiveYear(year);
    if (open) {
      setExpandedCard(null);
      setDrawerYear(year);
    }
  };

  // mobile: pehla tap sirf select karta hai, dusra tap drawer kholta hai
  const tapYear = (year) => {
    if (isMobile && year !== activeYear) selectYear(year, false);
    else selectYear(year, true);
  };

  const drawerData = drawerYear ? sortedYears.find((y) => y.year === drawerYear) : null;
  const drawerItems = drawerData ? drawerData.items.filter(matches) : [];
  const drawerInfo = drawerData ? YEAR_INFO[drawerData.year] || DEFAULT_INFO : DEFAULT_INFO;

  return (
    <div className="tl-page">
      {/* ================= CINEMATIC SCENE (laptop + mobile) ================= */}
      <section className="tl-scene">
        <div className="tl-bg" style={{ backgroundImage: 'url(/images/timeline-bg.jpg)' }} />
        <div className="tl-vignette" />
        <div
          className="tl-stage"
          ref={stageRef}
          onMouseMove={onStageMove}
          onMouseLeave={onStageLeave}
        >

          {/* ---------- SVG: dial, arc, beam, years ---------- */}
          <svg className="tl-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
            <defs>
              <filter id="tl-blur" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="7" />
              </filter>
              <filter id="tl-blur-s" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" />
              </filter>
              <radialGradient id="tl-halo">
                <stop offset="0%" stopColor="#fff1d6" stopOpacity="1" />
                <stop offset="25%" stopColor="#ff8a3d" stopOpacity="0.95" />
                <stop offset="60%" stopColor="#ff4d1a" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#ff4d1a" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="tl-arc-grad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#c9863a" />
                <stop offset="100%" stopColor="#ffb25a" />
              </linearGradient>
            </defs>

            {/* clock dial (top, partly cut) */}
            <g className="tl-dial">
              <circle cx={g.dial.cx} cy={g.dial.cy} r="345" fill="none" stroke="#b9772f" strokeOpacity="0.55" strokeWidth="2" />
              <circle cx={g.dial.cx} cy={g.dial.cy} r="330" fill="none" stroke="#d9a15a" strokeOpacity="0.55" strokeWidth="12" strokeDasharray="1.4 7.1" />
              <circle cx={g.dial.cx} cy={g.dial.cy} r="312" fill="none" stroke="#b9772f" strokeOpacity="0.4" strokeWidth="1.5" />
              <circle cx={g.dial.cx} cy={g.dial.cy} r="268" fill="none" stroke="#b9772f" strokeOpacity="0.18" strokeWidth="1" />
            </g>

            {/* glowing arc */}
            <path d={ARC_PATH} fill="none" stroke="#ff8a2a" strokeOpacity="0.55" strokeWidth="14" filter="url(#tl-blur)" />
            <path d={ARC_PATH} fill="none" stroke="url(#tl-arc-grad)" strokeWidth="11" strokeOpacity="0.55" strokeDasharray="1.2 6" />
            <path d={ARC_PATH} fill="none" stroke="#ffc680" strokeWidth="2.4" />

            {/* thin guide line (follows mouse) */}
            <line
              ref={trackRef}
              className="tl-track"
              x1={PIVOT.x} y1={PIVOT.y} x2={PIVOT.x} y2={PIVOT.y}
              stroke="#f3dcc0" strokeWidth="1.6"
            />

            {/* laser beam knob -> active year */}
            <motion.line
              x1={PIVOT.x} y1={PIVOT.y}
              initial={{ x2: activeNode.x, y2: activeNode.y }}
              animate={{ x2: activeNode.x, y2: activeNode.y }}
              transition={{ type: 'spring', stiffness: 90, damping: 16 }}
              stroke="#ff4d1a" strokeWidth="16" strokeOpacity="0.45" strokeLinecap="round"
              filter="url(#tl-blur)"
            />
            <motion.line
              x1={PIVOT.x} y1={PIVOT.y}
              initial={{ x2: activeNode.x, y2: activeNode.y }}
              animate={{ x2: activeNode.x, y2: activeNode.y }}
              transition={{ type: 'spring', stiffness: 90, damping: 16 }}
              stroke="#ff6a2b" strokeWidth="5" strokeLinecap="round"
            />
            <motion.line
              x1={PIVOT.x} y1={PIVOT.y}
              initial={{ x2: activeNode.x, y2: activeNode.y }}
              animate={{ x2: activeNode.x, y2: activeNode.y }}
              transition={{ type: 'spring', stiffness: 90, damping: 16 }}
              stroke="#ffe3c4" strokeWidth="1.8" strokeLinecap="round"
            />

            {/* knob */}
            <circle cx={PIVOT.x} cy={PIVOT.y} r="22" fill="url(#tl-halo)" opacity="0.7" />
            <circle cx={PIVOT.x} cy={PIVOT.y} r="10" fill="#fff4de" />

            {/* year nodes */}
            {nodes.map((nd) => (
              <circle
                key={nd.year}
                cx={nd.x} cy={nd.y} r="5"
                fill={availableYears.includes(nd.year) ? '#ffcf8a' : '#6b5435'}
              />
            ))}

            {/* glowing orb on active node */}
            <motion.circle
              r="40" fill="url(#tl-halo)" className="tl-orb"
              initial={{ cx: activeNode.x, cy: activeNode.y }}
              animate={{ cx: activeNode.x, cy: activeNode.y }}
              transition={{ type: 'spring', stiffness: 90, damping: 16 }}
            />
            <motion.circle
              r="7" fill="#fff6e6"
              initial={{ cx: activeNode.x, cy: activeNode.y }}
              animate={{ cx: activeNode.x, cy: activeNode.y }}
              transition={{ type: 'spring', stiffness: 90, damping: 16 }}
            />

            {/* year labels */}
            {nodes.map((nd) => {
              const isActive = nd.year === activeYear;
              const enabled = availableYears.includes(nd.year);
              return (
                <text
                  key={nd.year}
                  x={nd.x + g.label.dx}
                  y={nd.y + g.label.dy}
                  textAnchor="middle"
                  className={`tl-year-label ${isActive ? 'active' : ''} ${enabled ? '' : 'disabled'}`}
                  onMouseEnter={() => !isMobile && selectYear(nd.year, false)}
                  onClick={() => tapYear(nd.year)}
                >
                  {nd.year}
                </text>
              );
            })}
          </svg>

          {/* ---------- fan of year cards ---------- */}
          {(() => {
            const WIN = 4;
            const start = isMobile ? Math.min(Math.max(activeIndex - 1, 0), n - WIN) : 0;
            return isMobile ? sortedYears.slice(start, start + WIN) : sortedYears;
          })().map((y, i, list) => {
            const box = g.card(i, list.length);
            const info = YEAR_INFO[y.year] || DEFAULT_INFO;
            const isActive = y.year === activeYear;
            const enabled = availableYears.includes(y.year);
            return (
              <div
                key={y.year}
                className={`tl-card ${isActive ? 'active' : ''} ${enabled ? '' : 'disabled'}`}
                style={{
                  left: `${(box.x / W) * 100}%`,
                  top: `${(box.y / H) * 100}%`,
                  zIndex: isActive ? 60 : i + 1
                }}
                role="button"
                tabIndex={enabled ? 0 : -1}
                aria-label={`Open ${y.year}`}
                onMouseEnter={() => !isMobile && selectYear(y.year, false)}
                onFocus={() => !isMobile && selectYear(y.year, false)}
                onClick={() => tapYear(y.year)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectYear(y.year, true);
                  }
                }}
              >
                <div className="tl-card-img">
                  <img src={`/images/tl-${y.year}.jpg`} alt="" onError={hideBrokenImage} draggable="false" />
                </div>
                <div className="tl-card-body">
                  <span className="tl-card-year">{y.year}</span>
                  <span className="tl-card-label">{info.label}</span>
                  <ul className="tl-card-lines">
                    {info.lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
                <FaExternalLinkAlt className="tl-card-arrow" />
              </div>
            );
          })}

          {/* ---------- corner texts ---------- */}
          <div className="tl-title">
            <span>A</span>
            <span>Journey</span>
            <span>Through</span>
            <span>Time</span>
          </div>

          <nav className="tl-menu" aria-label="Filter timeline">
            {filterCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={activeFilter === cat.id ? 'active' : ''}
                onClick={() => setActiveFilter(cat.id)}
                title={`${counts[cat.id] || 0} items`}
              >
                {cat.label}
              </button>
            ))}
          </nav>

          <div className="tl-corner tl-corner-tr"><span>Move</span><span>to travel</span><span>through time</span></div>
          <div className="tl-corner tl-corner-bl"><span>Same</span><span>curiosity</span><span>a brighter</span><span>tomorrow</span></div>
          <div className="tl-corner tl-corner-br"><span>Still</span><span>designing</span><span>what&apos;s</span><span>next</span></div>
        </div>
      </section>

      {/* ================= STATS + SUMMARY ================= */}
      <section className="tl-footer">
        <div className="tl-stats">
          <div><b>{counts.all}</b><span>Milestones</span></div>
          <div><b>{counts.project || 0}</b><span>Projects</span></div>
          <div><b>{counts.education || 0}</b><span>Education</span></div>
          <div><b>{counts.certification || 0}</b><span>Certificates</span></div>
          <div><b>{counts.internship || 0}</b><span>Internships</span></div>
        </div>
        <div className="tl-summary">
          <h3>Journey Summary</h3>
          <p>
            Started as a <strong>BSc Mathematics student in 2019</strong>, discovered passion for coding in <strong>2021</strong>,
            completed undergraduate studies with <strong>82% marks in 2022</strong>, and immediately pursued <strong>MCA</strong>.
            Earned <strong>16+ certifications</strong> and completed <strong>8 virtual internships</strong> with Fortune 500 companies.
            Built <strong>7 production-ready projects</strong> including healthcare systems, e-commerce platforms, and banking applications.
            Graduated MCA with <strong>8.02 CGPA in 2024</strong> and continue to build innovative solutions.
          </p>
        </div>
      </section>

      {/* ================= DETAIL DRAWER ================= */}
      <AnimatePresence>
        {drawerData && (
          <motion.div
            className="tl-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerYear(null)}
          >
            <motion.aside
              className="tl-drawer"
              role="dialog"
              aria-modal="true"
              aria-label={`${drawerData.year} details`}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.35, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <header className="tl-drawer-head">
                <div>
                  <span className="tl-drawer-year">{drawerData.year}</span>
                  <span className="tl-drawer-label">{drawerInfo.label}</span>
                </div>
                <button type="button" className="tl-drawer-close" onClick={() => setDrawerYear(null)} aria-label="Close">
                  <FaTimes />
                </button>
              </header>

              <div className="tl-drawer-filters">
                {filterCategories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={activeFilter === cat.id ? 'active' : ''}
                    onClick={() => setActiveFilter(cat.id)}
                  >
                    {cat.icon}
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>

              <div className="tl-drawer-scroll">
                {drawerItems.length === 0 && (
                  <div className="tl-empty">
                    <FaFilter />
                    <h4>No items found</h4>
                    <p>Try selecting a different filter</p>
                    <button type="button" onClick={() => setActiveFilter('all')}>Show All</button>
                  </div>
                )}

                {drawerItems.map((item) => {
                  const isOpen = expandedCard === item.id;
                  const typeInfo = getTypeInfo(item.type);
                  return (
                    <article key={item.id} className="tl-item" style={{ '--card-color': item.color }}>
                      <div className="tl-item-head">
                        <div className="tl-item-icon" style={{ background: `${item.color}20`, color: item.color }}>
                          {item.icon}
                        </div>
                        <div className="tl-item-titles">
                          <span className="tl-item-badge" style={{ background: typeInfo.bg, color: typeInfo.color }}>
                            {typeInfo.label}
                          </span>
                          <h3>{item.title}</h3>
                          <p>{item.subtitle}</p>
                        </div>
                        {item.link && (
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="tl-item-ext" title="View Live">
                            <FaExternalLinkAlt />
                          </a>
                        )}
                      </div>

                      <div className="tl-item-meta">
                        <span><FaCalendarAlt /> {item.date}</span>
                        <span><FaMapMarkerAlt /> {item.location}</span>
                      </div>
                      <div className="tl-item-role"><FaBriefcase /> {item.role}</div>
                      <p className="tl-item-desc">{item.description}</p>

                      <div className="tl-item-highlights">
                        <h4>Key Highlights</h4>
                        {item.highlights.slice(0, isOpen ? item.highlights.length : 3).map((h) => (
                          <div key={h} className="tl-highlight">
                            <span style={{ background: `${item.color}25`, color: item.color }}>✓</span>
                            <span>{h}</span>
                          </div>
                        ))}
                      </div>

                      {item.highlights.length > 3 && (
                        <button
                          type="button"
                          className="tl-expand"
                          style={{ color: item.color }}
                          onClick={() => setExpandedCard(isOpen ? null : item.id)}
                        >
                          <span>{isOpen ? 'Show Less' : `+${item.highlights.length - 3} More Highlights`}</span>
                          <FaChevronDown className={isOpen ? 'rotated' : ''} />
                        </button>
                      )}

                      <div className="tl-tech">
                        {item.tech.map((t) => (
                          <span key={t} style={{ borderColor: `${item.color}60`, background: `${item.color}12`, color: item.color }}>
                            {t}
                          </span>
                        ))}
                      </div>

                      {item.link && (
                        <a href={item.link} target="_blank" rel="noopener noreferrer" className="tl-live-btn" style={{ background: item.color }}>
                          <FaExternalLinkAlt />
                          <span>View Live Project</span>
                        </a>
                      )}
                    </article>
                  );
                })}
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Timeline;